<template>
    <v-container class="pa-12">
        <v-card class="pl-16 pr-16 pt-8 pb-16">
            <v-card-title>
                <v-row>
                    <v-col cols="6" md="6">
                        <h3>Quotation / Fee Estimate</h3>
                    </v-col>
                    <v-col cols="6" md="6">
                        <v-row>
                            <v-col class="text-right" cols="12" md="12">
                                <h5>No. {{ doc.doc_no || ((docType === "fixed" ? "QT-" : "EST-") + "
                                    (ออกอัตโนมัติเมื่อบันทึก)") }}</h5>
                                <h5>Date {{ doc.doc_date_show }}</h5>
                                <v-chip x-small :color="statusColor" dark class="mt-1">{{ statusLabel }}</v-chip>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-btn-toggle v-model="docType" mandatory dense class="mb-4 ml-1 doctype-toggle"
                :disabled="!docTypeEditable">
                <v-btn small value="fixed">💰 Fixed Price (Quotation)</v-btn>
                <v-btn small value="estimate">⏱️ Estimate Fee</v-btn>
            </v-btn-toggle>

            <div v-if="docType === 'estimate'" class="wf-alert wf-alert-warn">
                ⚠️ เอกสารนี้เป็น <b>การประมาณการค่าบริการเบื้องต้น (Non-binding Estimate)</b>
                มิใช่ใบเสนอราคาที่มีผลผูกพันทางกฎหมาย และจะไม่ถูกนับรวมใน Pipeline หลักจนกว่าจะมี Invoice จริง
            </div>

            <!-- ===== Header fields: แก้ไขได้ทั้ง Draft และ Submitted ===== -->
            <v-row class="row1 mt-3 pt-3">
                <v-col cols="12" md="6">
                    <v-autocomplete v-model="doc.client_code" :items="clientOptions" item-text="code" item-value="code"
                        label="Client Code" outlined dense :disabled="!headerEditable"
                        @change="(val) => onClientSelect(val, 'code')"></v-autocomplete>
                </v-col>
                <v-col cols="12" md="6">
                    <v-autocomplete v-model="doc.client_name" :items="clientOptions" item-text="name" item-value="name"
                        label="Client Name" outlined dense :disabled="!headerEditable"
                        @change="(val) => onClientSelect(val, 'name')"></v-autocomplete>
                </v-col>
            </v-row>
            <v-row class="heightsize70formadvance">
                <v-col cols="12" md="10">
                    <v-text-field v-model="doc.client_address" label="Client Address" outlined dense
                        :disabled="!headerEditable"></v-text-field>
                </v-col>
            </v-row>
            <v-row class="heightsize70formadvance">
                <v-col cols="12" md="10">
                    <v-text-field v-model="doc.invoice_address" label="Invoice Address" outlined dense
                        background-color="#F5F5F5" disabled placeholder="auto-fill จาก Client Master"></v-text-field>
                </v-col>
            </v-row>
            <v-row class="heightsize70formadvance">
                <v-col cols="12" md="5">
                    <v-text-field v-model="doc.tax_id" label="Tax ID" outlined dense background-color="#F5F5F5"
                        disabled></v-text-field>
                </v-col>
                <v-col cols="12" sm="5">
                    <v-text-field v-model="doc.branch" label="Branch" outlined dense background-color="#F5F5F5"
                        disabled></v-text-field>
                </v-col>
            </v-row>
            <v-row class="heightsize70formadvance">
                <v-col cols="12" md="3">
                    <v-text-field v-model="doc.payment_term" label="Payment Term (Day)" outlined dense
                        :disabled="!headerEditable"></v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                    <v-select :items="currency_list" v-model="doc.currency" item-text="curr" item-value="curr"
                        label="Currency" outlined dense hide-details></v-select>
                </v-col>
                <v-col cols="12" sm="3">
                    <v-text-field v-model="doc.vat_percent" label="Vat (%)" outlined dense
                        :disabled="!headerEditable"></v-text-field>
                </v-col>
            </v-row>
            <v-row class="heightsize70formadvance">
                <v-col cols="6" md="6">
                    <v-text-field v-model="doc.attention" label="Attention" outlined dense
                        :disabled="!headerEditable"></v-text-field>
                </v-col>
                <v-col cols="6" md="6">
                    <v-text-field v-model="doc.contact_position" label="Contact Position" outlined dense
                        :disabled="!headerEditable"></v-text-field>
                </v-col>
            </v-row>
            <v-row class="heightsize70formadvance">
                <v-col cols="12" md="4">
                    <v-text-field v-model="doc.po_no" label="PO No." outlined dense
                        :disabled="!headerEditable"></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                    <v-menu v-model="menuPoDate" :close-on-content-click="false" offset-y min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field v-model="doc.po_date" label="PO Date" append-icon="mdi-calendar" readonly
                                outlined dense :disabled="!headerEditable" v-bind="attrs" v-on="on"></v-text-field>
                        </template>
                        <v-date-picker v-model="doc.po_date" @input="menuPoDate = false"></v-date-picker>
                    </v-menu>
                </v-col>
                <v-col cols="12" sm="4">
                    <v-menu v-model="menuDeliveryDate" :close-on-content-click="false" offset-y min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field v-model="doc.delivery_date" label="Delivery Date" append-icon="mdi-calendar"
                                readonly outlined dense :disabled="!headerEditable" v-bind="attrs"
                                v-on="on"></v-text-field>
                        </template>
                        <v-date-picker v-model="doc.delivery_date" @input="menuDeliveryDate = false"></v-date-picker>
                    </v-menu>
                </v-col>
            </v-row>

            <v-row class="row1 mt-3 pt-3">
                <v-col cols="12" md="7">
                    <v-textarea v-model="doc.additional_info" solo label="Additional information"
                        :disabled="!headerEditable"></v-textarea>
                </v-col>
            </v-row>
            <v-row class="row1 mt-3 pt-3">
                <v-col cols="12" md="3">
                    <input ref="fileInput" type="file" accept="application/pdf,.pdf" multiple style="display:none"
                        @change="onFileSelected" />
                    <v-btn :disabled="!headerEditable" class="btnuploadfile" elevation="7"
                        @click="$refs.fileInput.click()">
                        <v-icon left dark>mdi-cloud-upload</v-icon>Upload file
                    </v-btn>
                </v-col>
                <v-col cols="12" md="9">
                    <div class="file-list">
                        <div v-for="f in uploadedFiles" :key="f.id" class="file-card">
                            <div class="file-card-main" @click="previewFile(f)">
                                <v-icon color="#D32F2F" size="28">mdi-file-pdf-box</v-icon>
                                <span class="file-card-name" :title="f.name">{{ f.name }}</span>
                            </div>
                            <v-btn v-if="headerEditable" icon small class="file-card-delete" @click="removeFile(f.id)">
                                <v-icon small>mdi-delete-outline</v-icon>
                            </v-btn>
                        </div>
                    </div>
                </v-col>
            </v-row>

            <!-- ===== Fixed Price item table ===== -->
            <template v-if="docType === 'fixed'">
                <div class="d-flex justify-space-between align-center mt-4 mb-1">
                    <h3 class="text-subtitle-1 mb-0">รายการสินค้า/บริการ</h3>
                </div>
                <v-card outlined tile>
                    <v-simple-table dense>
                        <thead>
                            <tr class="bg-colorth">
                                <th class="texttablehcenter" style="width:24px"></th>
                                <th class="texttablehcenter" style="width:40px">No.</th>
                                <th class="texttablehcenter" style="min-width:150px">ชื่อสินค้า/รายละเอียด</th>
                                <th class="texttablehcenter text-right" style="width:100px">จำนวน</th>
                                <th class="texttablehcenter" style="width:100px">หน่วย</th>
                                <th class="texttablehcenter text-right" style="width:150px">ราคาต่อหน่วย</th>
                                <th class="texttablehcenter text-right" style="width:150px">ราคารวม</th>
                                <th class="texttablehcenter" style="width:36px"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, idx) in fixedRows" :key="row.id" :data-row-id="'fixed-' + row.id"
                                :draggable="itemsEditable" @dragstart="itemsEditable && onDragStart('fixed', row.id)"
                                @dragover.prevent="itemsEditable && onDragOver('fixed', row.id, $event)"
                                @drop="itemsEditable && onDrop('fixed', row.id, $event)" @dragend="onDragEnd"
                                :class="dragRowClass('fixed', row.id)">
                                <td class="drag-handle" v-if="itemsEditable">⇅</td>
                                <td v-else></td>
                                <td>{{ idx + 1 }}</td>
                                <td>
                                    <div class="item-picker">
                                        <v-combobox v-model="row.name" :items="filterMaster(row.searchText)"
                                            :search-input.sync="row.searchText" item-text="name" dense hide-details
                                            outlined :disabled="!itemsEditable" placeholder="ชื่อสินค้า"
                                            @input="(val) => onNameInput('fixed', row, val)">
                                            <template v-slot:item="{ item }">
                                                <span
                                                    :class="isAddNewItem(item) ? 'blue--text font-weight-medium' : ''">{{
                                                        item }}</span>
                                            </template>
                                        </v-combobox>
                                    </div>
                                    <v-textarea v-model="row.detail"
                                        :placeholder="row.name ? 'รายละเอียด (Shift+Enter ขึ้นบรรทัดใหม่)' : 'กรุณาเลือกชื่อสินค้าก่อน'"
                                        dense hide-details rows="1" auto-grow solo flat class="detail-input mt-1"
                                        :disabled="!itemsEditable || !row.name"
                                        @keydown="onDetailKeydown($event, 'fixed', row)"></v-textarea>
                                </td>
                                <td class="text-right">
                                    <v-text-field v-model.number="row.qty" type="number" dense hide-details outlined
                                        :disabled="!itemsEditable"></v-text-field>
                                </td>
                                <td>
                                    <v-text-field v-model="row.unit" dense hide-details outlined
                                        :disabled="!itemsEditable"></v-text-field>
                                </td>
                                <td class="text-right">
                                    <v-text-field v-model.number="row.price" type="number" dense hide-details outlined
                                        :disabled="!itemsEditable"></v-text-field>
                                </td>
                                <td class="text-right">{{ fmt(row.qty * row.price) }}</td>
                                <td>
                                    <v-icon v-if="itemsEditable" small color="error"
                                        @click="delRow('fixed', row.id)">mdi-close</v-icon>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card>
                <v-btn v-if="itemsEditable" small color="#90CAF9" class="mt-2" @click="addRow('fixed')">➕
                    เพิ่มแถว</v-btn>

                <!-- Total/VAT/Grand Total: คำนวณ real-time ตาม Business Logic Note 5 -->
                <v-row class="heightsize55formadvance mt-2">
                    <v-col class="text-right" cols="12" md="7"> </v-col>
                    <v-col class="text-right" cols="12" md="2">Total</v-col>
                    <v-col class="text-right" cols="12" md="3">
                        <b>{{ fmt(fixedTotals.total) }}</b>
                    </v-col>
                </v-row>
                <v-row class="heightsize55formadvance">
                    <v-col class="text-right" cols="12" md="7"> </v-col>
                    <v-col class="text-right" cols="12" md="2">VAT ({{ doc.vat_percent }}%)</v-col>
                    <v-col class="text-right" cols="12" md="3">
                        <b>{{ fmt(fixedTotals.vat) }}</b>
                    </v-col>
                </v-row>
                <v-row class="heightsize55formadvance">
                    <v-col class="text-right" cols="12" md="7"> </v-col>
                    <v-col class="text-right" cols="12" md="2">Grand total</v-col>
                    <v-col class="text-right" cols="12" md="3">
                        <span class="text-h6"><b>{{ fmt(fixedTotals.grandTotal) }}</b></span>
                    </v-col>
                </v-row>
            </template>

            <!-- ===== Estimate Fee item table ===== -->
            <template v-else>
                <div class="d-flex justify-space-between align-center mt-4 mb-1">
                    <h3 class="text-subtitle-1 mb-0">รายการบริการ (ประมาณการ)</h3>
                </div>
                <v-card outlined tile>
                    <v-simple-table dense>
                        <thead>
                            <tr class="bg-colorth">
                                <th class="texttablehcenter" style="width:24px"></th>
                                <th class="texttablehcenter">No.</th>
                                <th class="texttablehcenter" style="min-width:240px">ชื่อสินค้า/รายละเอียด</th>
                                <th class="texttablehcenter text-right" style="width:220px">(Rate/Hr.) / Estimate Fees
                                </th>
                                <th class="texttablehcenter" style="width:36px"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, idx) in estimateRows" :key="row.id" :data-row-id="'estimate-' + row.id"
                                :draggable="itemsEditable" @dragstart="itemsEditable && onDragStart('estimate', row.id)"
                                @dragover.prevent="itemsEditable && onDragOver('estimate', row.id, $event)"
                                @drop="itemsEditable && onDrop('estimate', row.id, $event)" @dragend="onDragEnd"
                                :class="dragRowClass('estimate', row.id)">
                                <td class="drag-handle" v-if="itemsEditable">⇅</td>
                                <td v-else></td>
                                <td>{{ idx + 1 }}</td>
                                <td>
                                    <div class="item-picker">
                                        <v-combobox v-model="row.name" :items="filterMaster(row.searchText)"
                                            :search-input.sync="row.searchText" item-text="name" dense hide-details
                                            outlined :disabled="!itemsEditable" placeholder="ชื่อสินค้า/บริการ"
                                            @input="(val) => onNameInput('estimate', row, val)">
                                            <template v-slot:item="{ item }">
                                                <span
                                                    :class="isAddNewItem(item) ? 'blue--text font-weight-medium' : ''">{{
                                                        item }}</span>
                                            </template>
                                        </v-combobox>
                                    </div>
                                    <v-textarea v-model="row.detail"
                                        :placeholder="row.name ? 'รายละเอียด (Shift+Enter ขึ้นบรรทัดใหม่)' : 'กรุณาเลือกชื่อสินค้าก่อน'"
                                        dense hide-details rows="1" auto-grow solo flat class="detail-input mt-1"
                                        :disabled="!itemsEditable || !row.name"
                                        @keydown="onDetailKeydown($event, 'estimate', row)"></v-textarea>
                                </td>
                                <td class="text-right">
                                    <v-text-field v-model="row.rate" dense hide-details outlined
                                        :disabled="!itemsEditable" type="number"></v-text-field>
                                </td>
                                <td>
                                    <v-icon v-if="itemsEditable" small color="error"
                                        @click="delRow('estimate', row.id)">mdi-close</v-icon>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card>
                <v-btn v-if="itemsEditable" small color="#90CAF9" class="mt-2" @click="addRow('estimate')">➕
                    เพิ่มแถว</v-btn>

                <v-row class="mt-4">
                    <v-col cols="12" md="6">
                        <v-text-field v-model="doc.not_to_exceed_cap"
                            label="วงเงินไม่เกิน (Not-to-Exceed Cap) — Optional" placeholder="ไม่ระบุ = ไม่จำกัดวงเงิน"
                            outlined dense :disabled="!itemsEditable"></v-text-field>
                    </v-col>
                </v-row>

                <div class="wf-alert wf-alert-info">
                    ℹ️ ไม่มีการคำนวณ Grand Total อัตโนมัติ เนื่องจากแต่ละรายการอาจใช้ฐานราคาต่างกัน —
                    ยอดที่เรียกเก็บจริงจะคำนวณตอนออก Invoice แต่ละรอบ
                </div>
            </template>

            <br />
            <hr />
            <br />
            <div>
                <v-row>
                    <v-col align="center" cols="12" md="12">
                        <v-btn outlined class="mr-5" @click="getBackList()">
                            Back
                        </v-btn>
                        <v-btn class="mr-5 buttonblue" :disabled="isLocked || doc.status !== 'Draft'"
                            @click="onSaveDraft">
                            Save Draft
                        </v-btn>
                        <v-btn v-if="isEditMode" depressed class="mr-5 buttonblue" :disabled="isLocked"
                            @click="onSubmit">
                            Submit
                        </v-btn>
                    </v-col>
                </v-row>

                <v-row v-if="showDelete">
                    <v-col align="end" cols="12" md="12">
                        <v-btn depressed class="buttonred" @click="dialogDelete = true">
                            Delete form
                        </v-btn>
                    </v-col>
                </v-row>
            </div>
        </v-card>

        <v-dialog v-model="dialogDelete" max-width="400px">
            <v-card style="padding-top: 30px; padding-bottom: 30px;">
                <v-card-title style="padding-bottom: 20px;">
                    <v-row justify="center">
                        <span class="red--text text-h5 ju">Delete this item?</span>
                    </v-row>
                </v-card-title>
                <br />
                <v-card-subtitle>
                    <v-row justify="center">
                        <span class="black--text">This action cannot be undone.</span>
                    </v-row>
                </v-card-subtitle>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn depressed width="100" class="mr-3 buttonblue" color="red" @click="confirmDelete">
                        Save
                    </v-btn>
                    <v-btn depressed outlined width="100" color="red" @click="dialogDelete = false">
                        Cancel
                    </v-btn>
                    <v-spacer></v-spacer>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3500">
            {{ snackbar.text }}
        </v-snackbar>

        <add-product-dialog v-model="showAddProductDialog" :prefill-name="addProductPrefill"
            @saved="onProductSaved"></add-product-dialog>
    </v-container>
</template>

<script>
import api from "@/services/api";
import { server, quotationUrl } from "@/services/constants";
import AddProductDialog from "./AddProductDialog.vue";
import Swal from "sweetalert2";

export default {
    name: "QuotationForm",
    components: { AddProductDialog },
    data() {
        return {
            currency_list: [],
            menuPoDate: false,
            menuDeliveryDate: false,
            dialogDelete: false,
            showAddProductDialog: false,
            addProductPrefill: "",
            addProductTarget: null, // { type, row }
            rowSeq: 100,
            ADD_NEW_PREFIX: '+ เพิ่ม "',
            ADD_NEW_SUFFIX: '" เป็นสินค้าใหม่',
            dragSrc: null,
            dragOver: null,

            snackbar: { show: false, text: "", color: "success" },

            docType: "fixed",

            doc: {
                client_id: 0,
                doc_no: "",
                status: "Draft",
                doc_date_show: this.getTodayShow(),
                client_code: "",
                client_name: "",
                client_address: "",
                invoice_address: "",
                tax_id: "",
                branch: "",
                payment_term: "0",
                currency: "THB",
                vat_percent: "7.00",
                attention: "",
                contact_position: "",
                po_no: "",
                po_date: "",
                delivery_date: "",
                additional_info: "",
                not_to_exceed_cap: "",
            },

            clientOptions: [],

            masterItems: [],

            fixedRows: [],
            estimateRows: [],
            uploadedFiles: [],
            removedFileIds: [],
        };
    },
    computed: {
        // เข้าทาง /add-quotation (ไม่มี :id) = โหมดสร้างใหม่ เสมอเป็น Draft ไม่มีปุ่มเลือกสถานะ
        // เข้าทาง /edit-quotation/:id = โหลดสถานะจริงจากเอกสาร (Draft/Submitted/Invoiced/InProgress)
        isEditMode() {
            return !!this.$route.params.id;
        },
        statusLabel() {
            return { Draft: "Draft", Submitted: "Submitted", Invoiced: "Invoiced", InProgress: "In Progress" }[this.doc.status];
        },
        statusColor() {
            return { Draft: "grey", Submitted: "blue", Invoiced: "green", InProgress: "purple" }[this.doc.status];
        },
        isLocked() {
            return this.doc.status === "Invoiced" || this.doc.status === "InProgress";
        },
        headerEditable() {
            return !this.isLocked; // Draft และ Submitted แก้ header ได้
        },
        itemsEditable() {
            return this.doc.status === "Draft"; // แก้รายการได้เฉพาะ Draft
        },
        docTypeEditable() {
            return this.doc.status === "Draft"; // ล็อกทันทีหลัง Submit
        },
        showDelete() {
            // ยังไม่มีเอกสารให้ลบตอนสร้างใหม่ (add-quotation) — โชว์ปุ่ม Delete เฉพาะตอนแก้ไขเอกสารที่มีอยู่แล้ว
            if (!this.isEditMode) return false;
            return !this.isLocked; // ไม่แสดงปุ่มลบเลยเมื่อ Invoiced/InProgress (REQ-2)
        },
        fixedTotals() {
            const total = this.fixedRows.reduce((s, r) => s + (Number(r.qty) || 0) * (Number(r.price) || 0), 0);
            const vat = total * (Number(this.doc.vat_percent) / 100 || 0);
            return { total, vat, grandTotal: total + vat };
        },
    },
    watch: {
        // ตรวจจับตอน AddProductDialog ปิด ไม่ว่าจากปุ่ม "ยกเลิก" หรือ ESC/click outside
        showAddProductDialog(val) {
            if (!val && this.addProductTarget) {
                // ถ้า addProductTarget ยังไม่ถูกเคลียร์ = ปิดโดยไม่ได้กด "บันทึก"
                // (onProductSaved จะ set addProductTarget = null ไปแล้วก่อนหน้านี้ถ้ากด save)
                // ต้องล้าง searchText ของแถวที่ trigger dialog ด้วย ไม่งั้นข้อความเก่าจะค้างในกล่อง
                this.addProductTarget.row.searchText = "";
                this.addProductTarget = null;
            }
        },
        '$route.params.id': {
            immediate: false,
            handler(newId, oldId) {
                if (newId !== oldId) {
                    this.reloadPage();
                }
            }
        }
    },
    async mounted() {
        try {
            await api.checkVersion();

            this.userId = localStorage.getItem(server.USER_ID);
            this.authorize_id = localStorage.getItem(server.AUTHORIZE_ID);

            if (this.authorize_id == null || this.authorize_id == 0) {
                this.$store.state.global_dialog = true;
                this.setupAlertDialog(
                    true,
                    "Authorize Failed!!!",
                    "Please Logout And Login Again!!!",
                    "text-h5 red--text text-center"
                );
                this.$router.back();
                return;
            }

            const parser = document.createElement("a");
            parser.href = window.location;
            const routerPath = parser.pathname.split("/")[1];

            const resAuth = await api.getAuthorize(this.userId, routerPath);
            const permission = resAuth.data[0];

            if (!permission) {
                this.$router.back();
                return;
            }

            this.authorize_view = permission.smd_view >= 1;
            this.authorize_add = permission.smd_add >= 1;
            this.authorize_edit = permission.smd_edit >= 1;
            this.authorize_del = permission.smd_del >= 1;

            if (!this.authorize_view) {
                this.$router.back();
                return;
            }

            if (this.userId && api.isLoggedIn()) {
                // already logged in
            } else {
                this.$store.state.isLogged = false;
                this.$router.push("/login");
                return;
            }
        } catch (error) {
            // ✅ catch นี้ครอบเฉพาะ auth flow เท่านั้น
            console.error("QuotationForm mounted() auth error:", error);
            this.$store.state.isLogged = false;
            this.$router.push("/login");
            return;
        }

        // ===== โหลดข้อมูลฟอร์ม แยก catch ออกจาก auth =====
        this.$showLoader();
        try {
            await this.loadCurrency();
            await this.loadCustomer();
            await this.loadMaterProductItem();
            if (this.isEditMode) {
                await this.loadQuotationForEdit(this.$route.params.id);
                await this.loadDataFile(this.$route.params.id)
            } else {
                this.doc.status = "Draft";
            }
        } catch (error) {
            // ✅ ไม่เกี่ยวกับ auth — แจ้ง error ตรงๆ ไม่เด้ง login
            console.error("QuotationForm mounted() load data error:", error);
            await Swal.fire({
                icon: "error",
                title: "เกิดข้อผิดพลาด",
                text: "ไม่สามารถโหลดข้อมูลฟอร์มได้ กรุณาลองใหม่อีกครั้ง",
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true,
            });
        } finally {
            this.$hideLoader();
        }
    },
    beforeDestroy() {
        // เคลียร์ object URL ทั้งหมดตอนออกจากหน้า กัน memory leak
        this.uploadedFiles.forEach((f) => URL.revokeObjectURL(f.url));
    },

    methods: {
        async reloadPage() {
            this.$showLoader();
            try {
                await this.loadCurrency();
                await this.loadCustomer();
                await this.loadMaterProductItem();
                if (this.isEditMode) {
                    await this.loadQuotationForEdit(this.$route.params.id);
                }
            } catch (error) {
                console.error("reloadPage error:", error);
                await Swal.fire({
                    icon: "error",
                    title: "เกิดข้อผิดพลาด",
                    text: "ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง",
                    showConfirmButton: false,
                    timer: 2500,
                    timerProgressBar: true,
                });
            } finally {
                this.$hideLoader();
            }
        },
        async loadCurrency() {
            const res_cur = await api.getDataCurrency();
            this.currency_list = res_cur.data;
        },
        async loadCustomer() {
            const result = await api.getAllCustomerListactiveCompany({
                params: {
                    company_id: localStorage.getItem(server.COMPANYID),
                },
            });

            this.clientOptions = result.data.map((item) => ({
                id: item.id,
                code: item.cust_account,
                name: item.name,
                address: item.address,
                tax_id: item.taxid,
                branch: item.branch,
                address_invoice: item.address_invoice,
                payment_term: item.payment_term,
                currency: item.currency,
                vat: item.vat,
                contact_position: item.contact_position,
                attention: item.contact_name,
            }));
        },
        async loadMaterProductItem() {
            const res = await api.billing_expense_getInvTitleByDep(localStorage.getItem(server.DEPARTMENT_ID));

            this.masterItems = res.data.map((item) => ({
                name: item.title,
                unit: item.unit,
                rate: Number(item.price),
            }));
        },
        async loadQuotationForEdit(id) {
            try {
                const res = await api.billing_expense_getAllQuotationsByID(id);
                const record = res.data;

                if (!record) {
                    await Swal.fire({
                        icon: "error",
                        title: "ไม่พบเอกสาร",
                        text: "ไม่พบเอกสารที่ต้องการแก้ไข",
                        showConfirmButton: false,
                        timer: 2500,
                        timerProgressBar: true,
                    });
                    this.$router.push("/quotation-list");
                    return;
                }

                // ===== Map header กลับเข้า this.doc =====
                this.doc = {
                    ...this.doc,
                    quotation_id: record.quotation_id,
                    doc_no: record.doc_no,
                    status: record.status,
                    doc_date_show: this.formatDocDateShow(record.doc_date),
                    client_id: record.client_id,
                    client_address: record.client_address,
                    payment_term: String(record.payment_term ?? "0"),
                    currency: record.currency,
                    vat_percent: String(record.vat_percent ?? "0"),
                    attention: record.attention,
                    contact_position: record.contact_position,
                    po_no: record.po_no,
                    po_date: record.po_date,
                    delivery_date: record.delivery_date,
                    additional_info: record.additional_info,
                    not_to_exceed_cap: record.not_to_exceed_cap != null ? String(record.not_to_exceed_cap) : "",
                };

                this.docType = record.doc_type; // "fixed" | "estimate"

                // ===== เติม client dropdown (ต้องรอ loadCustomer โหลดเสร็จก่อนหน้านี้แล้ว) =====
                const clientMatch = this.clientOptions.find((c) => c.id === record.client_id);
                if (clientMatch) {
                    this.doc.client_code = clientMatch.code;
                    this.doc.client_name = clientMatch.name;
                    this.doc.invoice_address = clientMatch.address_invoice;
                    this.doc.tax_id = clientMatch.tax_id;
                    this.doc.branch = clientMatch.branch;
                }

                // ===== Map items กลับเป็น fixedRows / estimateRows =====
                const items = record.items || [];

                if (this.docType === "fixed") {
                    this.fixedRows = items.map((item) => ({
                        id: item.item_row_id,
                        name: item.item_name,
                        searchText: "",
                        detail: item.detail || "",
                        qty: item.qty != null ? Number(item.qty) : 0,
                        unit: item.unit || "",
                        price: item.unit_price != null ? Number(item.unit_price) : 0,
                    }));
                    this.estimateRows = [];
                } else {
                    this.estimateRows = items.map((item) => ({
                        id: item.item_row_id,
                        name: item.item_name,
                        searchText: "",
                        detail: item.detail || "",
                        rate: item.line_total != null ? Number(item.line_total) : "",
                    }));
                    this.fixedRows = [];
                }

                // เผื่อ rowSeq ชนกับ item_row_id เดิม ตอนกด "เพิ่มแถว" ใหม่
                const maxId = Math.max(0, ...items.map((i) => i.item_row_id || 0));
                this.rowSeq = Math.max(this.rowSeq, maxId);

            } catch (error) {
                console.error("loadQuotationForEdit error:", error);
                await Swal.fire({
                    icon: "error",
                    title: "เกิดข้อผิดพลาด",
                    text: "ไม่สามารถโหลดข้อมูลเอกสารได้",
                    showConfirmButton: false,
                    timer: 2500,
                    timerProgressBar: true,
                });
                this.$router.push("/quotation-list");
            }
        },
        async loadDataFile(id) {
            try {
                const res = await api.billing_expense_load_file(id, Number(localStorage.getItem(server.COMPANYID)));
                const files = res.data || [];

                // ✅ Map ไฟล์ที่มีอยู่แล้วในระบบ (จาก backend) เข้า uploadedFiles
                // เพื่อให้แสดงผลใน file-list เหมือนไฟล์ที่เพิ่ง upload ใหม่
                this.uploadedFiles = files.map((f) => ({
                    id: `existing_${f.id}`,       // prefix กันชนกับไฟล์ใหม่ที่ใช้ Date.now()
                    fileId: f.id,                  // เก็บ id จริงจาก backend ไว้ใช้ตอนลบ/อ้างอิง
                    file: null,                    // ไฟล์นี้อยู่บน server แล้ว ไม่มี File object ดิบในเบราว์เซอร์
                    name: f.file_name,
                    url: `${server.API_URL}/billing-expense/quotations/file/${f.id}`, // ปรับตาม endpoint จริงที่ backend ใช้เสิร์ฟไฟล์
                    isExisting: true,              // flag บอกว่าเป็นไฟล์เดิม ไม่ใช่ไฟล์ใหม่ที่รอ upload
                }));
            } catch (error) {
                console.error("loadFile error:", error);
            }
        },
        getTodayShow() {
            const now = new Date();
            const bkkTime = new Date(now.getTime() + 7 * 60 * 60 * 1000);
            const dd = String(bkkTime.getUTCDate()).padStart(2, "0");
            const mm = String(bkkTime.getUTCMonth() + 1).padStart(2, "0");
            const yyyy = bkkTime.getUTCFullYear();
            return `${dd}/${mm}/${yyyy}`;
        },
        formatDateShow(isoDate) {
            if (!isoDate) return "";
            const d = new Date(isoDate);
            if (isNaN(d.getTime())) return "";
            const dd = String(d.getDate()).padStart(2, "0");
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            const yyyy = d.getFullYear();
            return `${dd}/${mm}/${yyyy}`;
        },
        onFileSelected(e) {
            const files = Array.from(e.target.files || []);
            const rejected = [];

            files.forEach((file) => {
                // อนุญาตเฉพาะไฟล์ PDF เท่านั้น เช็คทั้ง MIME type และนามสกุลไฟล์ (กันเคส MIME type ไม่ถูกต้องจากบาง OS)
                const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
                if (!isPdf) {
                    rejected.push(file.name);
                    return;
                }

                const sanitizedName = this.sanitizeFileName(file.name);
                const uniqueName = this.getUniqueFileName(sanitizedName);

                // ✅ สร้าง File object ใหม่ด้วยชื่อที่ unique แล้ว แทนที่จะใช้ file เดิม
                // เพื่อให้ไฟล์จริงที่จะส่งไป backend มีชื่อตรงกับที่แสดงบน UI (กันชื่อไฟล์ชนกันจริงๆ ตอนอัพโหลด)
                const renamedFile = new File([file], uniqueName, {
                    type: file.type,
                    lastModified: file.lastModified,
                });

                const url = URL.createObjectURL(renamedFile);
                this.uploadedFiles.push({
                    id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                    file: renamedFile, // ✅ เก็บไฟล์ที่ถูกเปลี่ยนชื่อแล้วจริงๆ
                    name: uniqueName,
                    url,
                });
            });

            if (rejected.length > 0) {
                Swal.fire({
                    icon: "error",
                    title: "เกิดข้อผิดพลาด",
                    text: `รองรับเฉพาะไฟล์ PDF เท่านั้น: ${rejected.join(", ")}`,
                    showConfirmButton: false,
                    timer: 2500,
                    timerProgressBar: true,
                });
            }

            e.target.value = "";
        },
        sanitizeFileName(originalName) {
            const dotIdx = originalName.lastIndexOf(".");
            const base = dotIdx > -1 ? originalName.slice(0, dotIdx) : originalName;
            const ext = dotIdx > -1 ? originalName.slice(dotIdx) : "";

            const sanitizedBase = base
                .replace(/[^a-zA-Z0-9ก-๙._-]+/g, "_")   // แทนที่ space/อักขระพิเศษด้วย _
                .replace(/_+/g, "_")                      // ยุบ _ ติดกันหลายตัวให้เหลือตัวเดียว
                .replace(/^_+|_+$/g, "");                 // ตัด _ นำหน้า/ท้ายทิ้ง

            return `${sanitizedBase || "file"}${ext.toLowerCase()}`; // กันเคสชื่อว่างเปล่าหลัง sanitize
        },

        getUniqueFileName(originalName) {
            const existingNames = new Set(this.uploadedFiles.map((f) => f.name));
            if (!existingNames.has(originalName)) return originalName;

            const dotIdx = originalName.lastIndexOf(".");
            const base = dotIdx > -1 ? originalName.slice(0, dotIdx) : originalName;
            const ext = dotIdx > -1 ? originalName.slice(dotIdx) : "";

            let counter = 1;
            let candidate = `${base}_${counter}${ext}`;
            while (existingNames.has(candidate)) {
                counter += 1;
                candidate = `${base}_${counter}${ext}`;
            }
            return candidate;
        },

        // เปิดไฟล์ preview ในแท็บใหม่
        previewFile(f) {
            let fileUrl = f.url;

            // ถ้าเป็นไฟล์เดิมที่โหลดจาก server ใช้ pattern URL ตามที่กำหนด
            if (f.isExisting) {
                fileUrl = `${quotationUrl}/${this.$route.params.id}/${f.name}`;
            }

            const previewWindow = window.open("", "_blank");

            if (!previewWindow) {
                Swal.fire({
                    icon: "warning",
                    title: "แจ้งเตือน",
                    text: "เบราว์เซอร์บล็อก popup กรุณาอนุญาตเปิดแท็บใหม่",
                    showConfirmButton: false,
                    timer: 2500,
                    timerProgressBar: true,
                });
                return;
            }

            // เขียน HTML เข้าไปในแท็บใหม่โดยตรง — address bar จะแสดงแค่ about:blank ไม่โชว์ URL ไฟล์จริง
            previewWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>${f.name}</title>
            <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { height: 100vh; display: flex; flex-direction: column; font-family: sans-serif; }
                .toolbar {
                    padding: 8px 16px;
                    background: #fafafa;
                    border-bottom: 1px solid #e0e0e0;
                    display: flex;
                    align-items: center;
                }
                .toolbar button {
                    padding: 6px 16px;
                    border: 1px solid #90a4ae;
                    background: #fff;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 14px;
                }
                .toolbar button:hover { background: #f0f0f0; }
                .frame-wrap { flex: 1; }
                iframe { width: 100%; height: 100%; border: none; }
            </style>
        </head>
        <body>
            <div class="toolbar">
                <button onclick="window.close()">← Back</button>
            </div>
            <div class="frame-wrap">
                <iframe src="${fileUrl}"></iframe>
            </div>
        </body>
        </html>
    `);
            previewWindow.document.close();
        },

        removeFile(id) {
            const idx = this.uploadedFiles.findIndex((f) => f.id === id);
            if (idx === -1) return;

            const target = this.uploadedFiles[idx];

            if (target.isExisting) {
                this.removedFileIds.push({
                    module_name: "Quotation",
                    module_id: this.$route.params.id,
                    file_id: target.fileId,
                    filename: target.name,
                });
            } else {
                URL.revokeObjectURL(target.url);
            }

            this.uploadedFiles.splice(idx, 1);
        },
        getBackList() {
            this.$router.push("/quotation-list");
        },
        fmt(n) {
            return (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        onClientSelect(value, key = "code") {
            const c =
                key === "code"
                    ? this.clientOptions.find((x) => x.code === value)
                    : this.clientOptions.find((x) => x.name === value);

            if (c) {
                this.doc.client_id = c.id
                this.doc.client_code = c.code;
                this.doc.client_name = c.name;
                this.doc.client_address = c.address;
                this.doc.invoice_address = c.address_invoice;
                this.doc.tax_id = c.tax_id;
                this.doc.branch = c.branch;
                this.doc.payment_term = c.payment_term;
                this.doc.currency = c.currency;
                this.doc.vat_percent = c.vat;
                this.doc.contact_position = c.contact_position;
                this.doc.attention = c.attention;
            } else {
                this.doc.client_code = "";
                this.doc.client_name = "";
                this.doc.client_address = "";
                this.doc.invoice_address = "";
                this.doc.tax_id = "";
                this.doc.branch = "";
                this.doc.payment_term = "0";
                this.doc.currency = "THB";
                this.doc.vat_percent = "";
                this.doc.contact_position = "";
                this.doc.attention = "";
            }
        },

        filterMaster(text) {
            const t = (text || "").toString().trim().toLowerCase();
            const original = (text || "").toString().trim();
            if (!t) return this.masterItems.map((m) => m.name);
            const matches = this.masterItems.filter((m) => m.name.toLowerCase().includes(t)).map((m) => m.name);
            if (matches.length === 0 && original) {
                return [`${this.ADD_NEW_PREFIX}${original}${this.ADD_NEW_SUFFIX}`];
            }
            return matches;
        },
        // เช็คว่า item ที่ render อยู่ใน dropdown เป็นตัวเลือก "+ เพิ่มสินค้าใหม่" หรือเปล่า (ใช้แต่งสีฟ้า)
        isAddNewItem(item) {
            return (
                typeof item === "string" &&
                item.startsWith(this.ADD_NEW_PREFIX) &&
                item.endsWith(this.ADD_NEW_SUFFIX)
            );
        },
        onNameInput(type, row, val) {
            if (
                typeof val === "string" &&
                val.startsWith(this.ADD_NEW_PREFIX) &&
                val.endsWith(this.ADD_NEW_SUFFIX)
            ) {
                const typed = val.slice(this.ADD_NEW_PREFIX.length, val.length - this.ADD_NEW_SUFFIX.length);
                row.name = "";
                this.openAddProduct(type, row, typed);
                return;
            }
            row.name = val;
            const found = this.masterItems.find((m) => m.name === val);
            if (found) {
                if (type === "fixed") {
                    row.unit = found.unit;
                    row.price = found.rate;   // <-- เพิ่ม: auto-fill ราคาต่อหน่วย
                } else {
                    row.rate = found.rate;    // <-- เพิ่ม: auto-fill ราคาสำหรับ estimate
                }
            }
        },
        // Note 21: กด Enter เฉยๆ ย้าย focus ไปแถวถัดไป (หรือเพิ่มแถวใหม่ถ้าเป็นแถวสุดท้าย)
        // กด Shift+Enter ขึ้นบรรทัดใหม่ในช่องรายละเอียดตามปกติ
        onDetailKeydown(e, type, row) {
            if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                const arr = type === "fixed" ? this.fixedRows : this.estimateRows;
                const idx = arr.findIndex((r) => r.id === row.id);
                const next = arr[idx + 1];
                if (next) {
                    this.$nextTick(() => {
                        const el = document.querySelector(`tr[data-row-id="${type}-${next.id}"] .item-picker input`);
                        if (el) el.focus();
                    });
                } else {
                    this.addRow(type);
                }
            }
        },
        // ---- Row add/remove ----
        addRow(type) {
            this.rowSeq += 1;
            if (type === "fixed") {
                this.fixedRows.push({ id: this.rowSeq, name: "", searchText: "", detail: "", qty: 1, unit: "", price: 0 });
            } else {
                this.estimateRows.push({ id: this.rowSeq, name: "", searchText: "", detail: "", rate: "" });
            }
        },
        delRow(type, id) {
            if (type === "fixed") this.fixedRows = this.fixedRows.filter((r) => r.id !== id);
            else this.estimateRows = this.estimateRows.filter((r) => r.id !== id);
        },
        // ---- Add Product modal ----
        openAddProduct(type, row, prefill) {
            this.addProductTarget = { type, row };
            this.addProductPrefill = prefill || "";
            this.showAddProductDialog = true;
        },
        async onProductSaved(product) {
            // เติมค่าลงแถวที่ trigger dialog ทันที ไม่ต้องรอ API โหลด masterItems ใหม่
            // กันปัญหา field ว่างค้างระหว่างรอ network (dialog ปิดไปแล้วตั้งแต่ก่อน onProductSaved ทำงานเสร็จ)
            if (this.addProductTarget) {
                const { type, row } = this.addProductTarget;
                row.name = product.name;
                row.searchText = product.name; // sync search text ให้ตรงกับชื่อที่เพิ่งเลือก ไม่ให้ค้างข้อความเก่า
                if (type === "fixed") {
                    row.unit = product.unit;
                    row.price = product.rate;
                } else {
                    row.rate = product.rate;
                }
            }
            this.addProductTarget = null;

            // โหลด masterItems ใหม่แบบ background เพื่อให้ dropdown ครั้งถัดไปมีตัวเลือกล่าสุดจาก backend
            // ไม่ต้อง await ก่อนแสดงผลแถว เพราะแถวถูกเติมค่าไปแล้วด้านบน
            try {
                await this.loadMaterProductItem();
            } catch (error) {
                console.error("loadMaterProductItem error:", error);
            }
        },
        // ---- Drag & drop reorder ----
        onDragStart(type, id) {
            this.dragSrc = { type, id };
        },
        onDragOver(type, id) {
            if (!this.dragSrc || this.dragSrc.type !== type) return;
            this.dragOver = id;
        },
        onDrop(type, id) {
            if (!this.dragSrc || this.dragSrc.type !== type || this.dragSrc.id === id) return;
            const arr = type === "fixed" ? this.fixedRows : this.estimateRows;
            const fromIdx = arr.findIndex((r) => r.id === this.dragSrc.id);
            const toIdx = arr.findIndex((r) => r.id === id);
            const [moved] = arr.splice(fromIdx, 1);
            arr.splice(toIdx, 0, moved);
            this.dragSrc = null;
            this.dragOver = null;
        },
        onDragEnd() {
            this.dragSrc = null;
            this.dragOver = null;
        },
        dragRowClass(type, id) {
            return this.dragOver === id && this.dragSrc && this.dragSrc.type === type ? "drop-target" : "";
        },
        // ---- Validation / Save / Submit / Delete ----
        validateBeforeSubmit() {
            if (!this.doc.client_code) {
                Swal.fire({
                    icon: "error",
                    title: "เกิดข้อผิดพลาด",
                    text: "กรุณาเลือก Client ก่อน Submit",
                    showConfirmButton: false,
                    timer: 2500,
                    timerProgressBar: true,
                });
                return false;
            }
            const rows = this.docType === "fixed" ? this.fixedRows : this.estimateRows;
            if (rows.length === 0) {
                Swal.fire({
                    icon: "error",
                    title: "เกิดข้อผิดพลาด",
                    text: "กรุณาเพิ่มรายการอย่างน้อย 1 รายการ",
                    showConfirmButton: false,
                    timer: 2500,
                    timerProgressBar: true,
                });
                return false;
            }
            return true;
        },
        // แปลง "" / undefined / null เป็น null สำหรับ field ตัวเลข กัน "invalid input syntax for type numeric"
        toNullableNumber(val) {
            if (val === "" || val === undefined || val === null) return null;
            const n = Number(val);
            return isNaN(n) ? null : n;
        },

        // แปลงวันที่ว่างเปล่า/parse ไม่ได้ เป็น null กัน "invalid input syntax for type date"
        toNullableDate(val) {
            if (!val || val === "" || val === "Invalid date") return null;
            const d = new Date(val);
            if (isNaN(d.getTime())) return null;
            return val; // v-date-picker คืนเป็น "YYYY-MM-DD" อยู่แล้ว ส่งตรงได้ถ้า valid
        },

        // แปลง "" / undefined เป็น null สำหรับ field ข้อความที่เป็น optional (nullable ใน DB)
        toNullableString(val) {
            return val === "" || val === undefined ? null : val;
        },

        // ปัดเศษทศนิยม 2 ตำแหน่งตามหลักคณิตศาสตร์ (round half up) กัน floating point error
        round2(num) {
            const n = Number(num) || 0;
            return Math.round((n + Number.EPSILON) * 100) / 100;
        },
        getTodayDateOnly() {
            const now = new Date();
            const bkkTime = new Date(now.getTime() + 7 * 60 * 60 * 1000);
            const yyyy = bkkTime.getUTCFullYear();
            const mm = String(bkkTime.getUTCMonth() + 1).padStart(2, "0");
            const dd = String(bkkTime.getUTCDate()).padStart(2, "0");
            return `${yyyy}-${mm}-${dd}`;
        },
        formatDocDateShow(dateOnly) {
            if (!dateOnly) return "";
            const [yyyy, mm, dd] = dateOnly.split("-");
            if (!yyyy || !mm || !dd) return "";
            return `${dd}/${mm}/${yyyy}`;
        },

        async onSaveDraft() {
            if (!this.validateBeforeSubmit()) return;

            await this.saveQuotation('Draft');
        },


        async onSubmit() {
            // ✅ ต้องเช็คก่อนเสมอ ห้ามแก้ตารางแล้วข้อมูลไม่ครบหลุดเข้าไปเป็น Submitted
            if (!this.validateBeforeSubmit()) return;

            if (this.docType === "fixed" && this.fixedTotals.grandTotal === 0) {
                // Edge case: Grand Total = 0 — ต้องให้ผู้ใช้ยืนยันก่อน เพราะหลัง Submit แก้ไขรายการไม่ได้แล้ว
                if (!window.confirm("ยอดรวมเป็น 0 ต้องการดำเนินการต่อหรือไม่")) return;
            }

            await this.saveQuotation('Submitted');
        },

        async saveQuotation(status) {
            var dataItem = {
                doc_type: this.docType,
                status: status, // ✅ ส่วนต่างเดียวระหว่าง Draft กับ Submit
                client_id: Number(this.doc.client_id),
                client_address: this.doc.client_address,
                payment_term: this.doc.payment_term,
                currency: this.doc.currency,
                vat_percent: Number(this.doc.vat_percent),
                attention: this.doc.attention,
                contact_position: this.doc.contact_position,
                po_no: this.doc.po_no,
                po_date: this.toNullableDate(this.doc.po_date),
                delivery_date: this.toNullableDate(this.doc.delivery_date),
                additional_info: this.doc.additional_info,
                subtotal: 0,
                vat_amount: 0,
                grand_total: 0,
                not_to_exceed_cap: 0,
                user_update: Number(localStorage.getItem(server.USER_ID)),
                company_id: Number(localStorage.getItem(server.COMPANYID)),
                department_id: Number(localStorage.getItem(server.DEPARTMENT_ID)),
            };

            if (this.docType == "fixed") {
                dataItem.subtotal = this.round2(this.fixedTotals.total);
                dataItem.vat_amount = this.round2(this.fixedTotals.vat);
                dataItem.grand_total = this.round2(this.fixedTotals.grandTotal);
            } else if (this.docType == "estimate") {
                dataItem.not_to_exceed_cap = Number(this.doc.not_to_exceed_cap);
            } else {
                return;
            }

            const sourceRows = this.docType === "fixed" ? this.fixedRows : this.estimateRows;
            const buildItemsPayload = (quotationId) =>
                sourceRows.map((row, idx) => {
                    const base = {
                        quotation_id: quotationId,
                        seq_no: idx + 1,
                        item_name: row.name || "",
                        detail: this.toNullableString(row.detail),
                    };
                    if (this.docType === "fixed") {
                        const qty = this.toNullableNumber(row.qty);
                        const unitPrice = this.toNullableNumber(row.price);
                        return {
                            ...base,
                            qty: qty,
                            unit: this.toNullableString(row.unit),
                            unit_price: unitPrice,
                            line_total: this.round2((qty || 0) * (unitPrice || 0)),
                        };
                    } else {
                        return {
                            ...base,
                            qty: null,
                            unit: null,
                            unit_price: null,
                            line_total: this.toNullableNumber(row.rate),
                        };
                    }
                });

            const isEdit = !!this.$route.params.id;
            const successTitle = status === 'Draft'
                ? (isEdit ? "บันทึกสำเร็จ" : "เพิ่มสำเร็จ")
                : "Submit สำเร็จ";
            const successText = status === 'Draft'
                ? (isEdit ? "บันทึกการแก้ไข Draft เรียบร้อยแล้ว" : "เพิ่มใบเสนอราคาเรียบร้อยแล้ว")
                : "ส่งเอกสารเรียบร้อยแล้ว รายการถูกล็อกแล้ว";

            this.$showLoader(); // ✅ เริ่มโหลดก่อนยิง API

            // ===== โหมดแก้ไข (Edit) =====
            if (isEdit) {
                const editId = this.$route.params.id;

                try {
                    const res_update = await api.billing_expense_updateQuotations(editId, dataItem);

                    if (res_update.status == 200) {
                        const itemsPayload = buildItemsPayload(Number(editId));

                        try {
                            await api.billing_expense_deleteQuotationsItem(editId);
                            if (itemsPayload.length > 0) {
                                await api.billing_expense_addQuotationsItem(itemsPayload);
                            }

                            // ✅ ลบไฟล์เดิมที่ผู้ใช้กด remove ไว้ (ยิงตอนนี้ก่อน Save จริง)
                            for (const removed of this.removedFileIds) {
                                await api.billing_expense_delete_file(removed); // removed = { module_name, module_id, file_id, filename }
                            }
                            this.removedFileIds = [];

                            // ✅ อัพโหลดเฉพาะไฟล์ใหม่ที่ยังไม่มี isExisting (ไฟล์เดิมไม่ต้อง upload ซ้ำ)
                            const newFiles = this.uploadedFiles.filter((f) => !f.isExisting);
                            for (const item of newFiles) {
                                let formData = new FormData();
                                formData.append("file", item.file);
                                formData.append("id", Number(this.$route.params.id));
                                formData.append("user_id", Number(localStorage.getItem(server.USER_ID)));
                                formData.append("company_id", Number(localStorage.getItem(server.COMPANYID)));

                                await api.billing_expense_upload_file(formData);
                            }

                            await this.loadQuotationForEdit(editId);
                            await this.loadDataFile(editId);
                            this.$hideLoader();

                            await Swal.fire({
                                icon: "success",
                                title: successTitle,
                                text: successText,
                                showConfirmButton: false,
                                timer: 2000,
                                timerProgressBar: true,
                            });

                        } catch (errorItems) {
                            console.error("update items error:", errorItems);
                            this.$hideLoader();
                            await Swal.fire({
                                icon: "error",
                                title: "เกิดข้อผิดพลาด",
                                text: "บันทึก Header สำเร็จ แต่บันทึกรายการล้มเหลว กรุณาลองใหม่อีกครั้ง",
                                showConfirmButton: false,
                                timer: 2500,
                                timerProgressBar: true,
                            });
                        }
                    }
                } catch (error) {
                    console.error("updateQuotations error:", error);
                    this.$hideLoader();
                    await Swal.fire({
                        icon: "error",
                        title: "เกิดข้อผิดพลาด",
                        text: error.response?.data?.message || "ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง",
                        showConfirmButton: false,
                        timer: 2500,
                        timerProgressBar: true,
                    });
                }

            } else {
                // ===== โหมดสร้างใหม่ (Create) — ใช้ได้กับปุ่ม Save Draft เท่านั้น เพราะปุ่ม Submit ไม่โชว์ตอนสร้างใหม่ (v-if="isEditMode") =====
                dataItem.doc_date = this.getTodayDateOnly();
                dataItem.doc_no = this.doc.doc_no;
                dataItem.user_create = Number(localStorage.getItem(server.USER_ID));

                try {
                    const res_add = await api.billing_expense_addQuotations(dataItem);

                    if (res_add.status == 201) {
                        var main_id = Number(res_add.data.quotation_id);
                        const itemsPayload = buildItemsPayload(main_id);

                        try {
                            for (const item of this.uploadedFiles) {
                                let formData = new FormData();
                                formData.append("file", item.file); // item.file คือ File object ดิบที่เก็บไว้ตอน onFileSelected
                                formData.append("id", Number(main_id));
                                formData.append("user_id", Number(localStorage.getItem(server.USER_ID)));
                                formData.append("company_id", Number(localStorage.getItem(server.COMPANYID)));

                                await api.billing_expense_upload_file(formData);
                            }

                            const res_items = await api.billing_expense_addQuotationsItem(itemsPayload);

                            if (res_items.status == 201) {
                                this.uploadedFiles = []
                                await this.loadDataFile(Number(main_id));
                                this.$hideLoader();
                                await Swal.fire({
                                    icon: "success",
                                    title: successTitle,
                                    text: successText,
                                    showConfirmButton: false,
                                    timer: 2000,
                                    timerProgressBar: true,
                                });
                                this.$router.push(`/edit-quotation/${main_id}`);
                            }
                        } catch (errorItems) {
                            console.error("addQuotationsItem error:", errorItems);
                            this.$hideLoader();
                            await Swal.fire({
                                icon: "error",
                                title: "เกิดข้อผิดพลาด",
                                text: `บันทึก Header สำเร็จ (เลขที่ ${res_add.data.doc_no}) แต่บันทึกรายการล้มเหลว กรุณาแก้ไขเอกสารนี้อีกครั้ง`,
                                showConfirmButton: false,
                                timer: 3000,
                                timerProgressBar: true,
                            });
                            this.$router.push(`/edit-quotation/${main_id}`);
                        }
                    }
                } catch (error) {
                    console.error("addQuotations error:", error);
                    this.$hideLoader();
                    await Swal.fire({
                        icon: "error",
                        title: "เกิดข้อผิดพลาด",
                        text: error.response?.data?.message || "ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง",
                        showConfirmButton: false,
                        timer: 2500,
                        timerProgressBar: true,
                    });
                }
            }
        },
        async confirmDelete() {
            const id = this.$route.params.id;
            this.dialogDelete = false; // ปิด dialog ยืนยันก่อน แล้วค่อยแสดง loader
            this.$showLoader();

            try {
                const res = await api.billing_expense_updateQuotations(id, { is_deleted: true });

                if (res.status == 200) {
                    this.$hideLoader();
                    await Swal.fire({
                        icon: "success",
                        title: "ลบสำเร็จ",
                        text: "ลบใบเสนอราคาเรียบร้อยแล้ว",
                        showConfirmButton: false,
                        timer: 2000,
                        timerProgressBar: true,
                    });
                    this.$router.push("/quotation-list");
                }
            } catch (error) {
                console.error("confirmDelete error:", error);
                this.$hideLoader();
                await Swal.fire({
                    icon: "error",
                    title: "เกิดข้อผิดพลาด",
                    text: error.response?.data?.message || "ไม่สามารถลบข้อมูลได้ กรุณาลองใหม่อีกครั้ง",
                    showConfirmButton: false,
                    timer: 2500,
                    timerProgressBar: true,
                });
            }
        },
    },
    async beforeCreate() {
        this.$store.state.navMenu = true;
    },

};
</script>

<style scoped>
.bg-colorth {
    background-color: #595959;
}

.texttablehcenter {
    color: #fff !important;
    text-align: center;
}

.drag-handle {
    cursor: grab;
    color: #90a4ae;
    text-align: center;
}

.drop-target {
    box-shadow: inset 0 2px 0 #1565c0;
}

.detail-input>>>textarea {
    font-size: 12px;
    color: #1565c0;
}

.item-picker {
    position: relative;
}

.v-btn:not(.v-btn--round).v-size--default.buttonblue {
    background-color: #1d2442;
    color: white;
}

.v-btn:not(.v-btn--round).v-size--default.buttonred {
    background-color: #9e3b1b;
    color: white;
}

.heightsize70formadvance {
    height: 70px;
}

.heightsize55formadvance {
    height: 55px;
}

.btnuploadfile {
    border: 2px solid;
}

.hover-back:hover {
    text-decoration: underline;
}

/* ปุ่ม toggle ประเภทเอกสาร ตอนเลือกอยู่ = สีม่วง ตรงกับ wireframe (.doctype-pill active #6A1B9A) */
.doctype-toggle>>>.v-btn--active {
    background-color: #6a1b9a !important;
    color: #fff !important;
}

.doctype-toggle>>>.v-btn--active::before {
    opacity: 0;
}

/* Alert boxes — สีตรงกับ wireframe ต้นฉบับเป๊ะ (.alert-info / .alert-warn) */
.wf-alert {
    border-radius: 6px;
    padding: 10px 14px;
    font-size: 13px;
    margin-bottom: 14px;
}

.wf-alert-info {
    background: #e3f2fd;
    color: #0d47a1;
    border-left: 4px solid #1565c0;
}

.wf-alert-warn {
    background: #fff3e0;
    color: #e65100;
    border-left: 4px solid #ef6c00;
}

.file-list {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.file-card {
    display: flex;
    align-items: center;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 6px 6px 6px 12px;
    max-width: 260px;
    transition: box-shadow 0.15s, border-color 0.15s;
}

.file-card:hover {
    border-color: #90caf9;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.file-card-main {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    overflow: hidden;
    flex: 1;
    min-width: 0;
}

.file-card-name {
    font-size: 13px;
    color: #1565c0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.file-card-delete {
    margin-left: 6px;
    flex-shrink: 0;
}

.file-card-delete .v-icon {
    color: #b0bec5;
}

.file-card-delete:hover .v-icon {
    color: #e53935;
}
</style>